import isEqual from "lodash-es/isEqual";
import type { Maybe } from "@/components/utils/typeHelpers";

export interface IHasDirtyCheck {
  isDirty(): boolean;
  setDirtyCheckpoint(): void;
}

export interface IModelObject<T = any> {
  validate(): string[];
  clone(): T;
  toProto(): any;
  toBinary(): Uint8Array;
  equals(other: T): boolean;
}

export type ValidationRule = {
  field: string;
  rule: (val: any) => boolean | string | string[];
}[];

export abstract class ModelObject<
  T extends IModelObject,
> implements IModelObject<T> {
  abstract validationRules: ValidationRule;

  /**
   * Validates this object to ensure it's in a good state to be turned into a model object.
   */
  public validate(): string[] {
    const errors: string[] = [];
    for (let index = 0; index < this.validationRules.length; index++) {
      const { field, rule } = this.validationRules[index];
      const retVal = rule((this as any)[field]);
      if (typeof retVal === "string") {
        errors.push(retVal);
      } else if (Array.isArray(retVal)) {
        errors.push(...retVal);
      }
    }

    return errors;
  }

  public clone(): T {
    // @ts-ignore
    return new this.constructor(this.toProto());
  }

  abstract toProto(): any;

  abstract toBinary(): Uint8Array;

  public equals(other: Maybe<T>): boolean {
    return !!other && isEqual(this.toBinary(), other.toBinary());
  }
}

export abstract class ModelObjectWithDirtyCheck<T extends IModelObject>
  extends ModelObject<T>
  implements IModelObject<T>, IHasDirtyCheck
{
  private checkpoint?: string;

  /**
   * Whether this object has changed since the last checkpoint (or creation).
   */
  public isDirty(): boolean {
    return this.checkpoint !== JSON.stringify(this.toProto());
  }

  /**
   * Sets a checkpoint in the object which will make the current state "clean". All future changes will make the
   * object dirty.
   */
  public setDirtyCheckpoint(): void {
    this.checkpoint = JSON.stringify(this.toProto());
  }
}
